import React, { useEffect, useState } from "react";
import { Button, CloseButton, Col, Form, FormGroup, InputGroup, Modal, Row, Stack, Tab, Tabs, ToggleButton } from "react-bootstrap";
import { BlockTypes, FontStyle, LayoutJsonModel, LayoutStructureImageModel, LayoutStructureModel, LayoutStructureTextModel, ShadowStyle, TextConstraint } from "../../models/BlockTransportSystemJsonModel";
import LayoutRenderer from "../LayoutRenderer/LayoutRenderer";
import { t } from "i18next";
import { getBlockTypeConfigs } from "../../constants/BlockTypeConfig";

interface IProps {
  isShow: boolean;
  selectedLayout?: LayoutJsonModel;
  onClickCreate: (layout?: LayoutJsonModel) => void;
  onClickSave: (layout?: LayoutJsonModel) => void;
  onClickClose: () => void;
}

const LayoutEditModal = ({
  isShow,
  selectedLayout,
  onClickCreate,
  onClickSave,
  onClickClose
}: IProps) => {
  const [blockType, setBlockType] = useState<string>(selectedLayout?.blockType ?? BlockTypes.none);
  const [layoutType, setLayoutType] = useState<string>(selectedLayout?.layoutType ?? "");
  const [layoutStructures, setLayoutStructures] = useState<LayoutStructureModel[]>(selectedLayout?.layouts ?? []);
  const [activeLayoutId, setActiveLayoutId] = useState<string>(layoutStructures?.length > 0 ? layoutStructures[0]?.id : "");

  // Layout 추가
  const addLayout = () => {
    const newLayout: LayoutStructureModel = {
      id: `layout-${Date.now()}`,
      type: "",
      width: 1844,
      height: 1240,
      images: [],
      texts: []
    };
    setLayoutStructures([...layoutStructures, newLayout]);
    setActiveLayoutId(newLayout.id);

  };

  const closeLayout = (id: string) => {
    setLayoutStructures(prev => prev.filter(l => l.id !== id));
    // 현재 활성화된 레이아웃을 닫으면 첫 번째 레이아웃으로 이동
    if (activeLayoutId === id && layoutStructures.length > 1) {
      const remaining = layoutStructures.filter(l => l.id !== id);
      setActiveLayoutId(remaining[0].id);
    }
  };

  // Text 추가
  const addText = (layoutId: string) => {
    setLayoutStructures(layoutStructures.map((layout: LayoutStructureModel) => {
      if (layout.id === layoutId) {
        const newText: LayoutStructureTextModel = {
          id: `text-${Date.now()}`,
          type: "label",
          x: 0,
          y: 0,
          width: 100,
          height: 30,
          rotation: 0,
          text: "New Text",
          horizontalAlign: "center",
          verticalAlign: "middle",
          constraint: {
            limitLength: 20,
            isDateFormat: false,
            dateFormat: null,
            allowedCharacterSets: null
          },
          font: {
            family: "Arial",
            size: 14,
            color: "#000000"
          },
          shadow: {
            color: "#333333",
            offsetX: 2,
            offsetY: 2,
            blur: 5
          }
        };
        return { ...layout, texts: [...layout.texts, newText] };
      }
      return layout;
    }));
  };

  // Text 수정
  const updateText = (
    layoutId: string,
    textId: string,
    field: keyof LayoutStructureTextModel,
    value: any
  ) => {
    setLayoutStructures(layoutStructures.map(layout => {
      if (layout.id === layoutId) {
        return {
          ...layout,
          texts: layout.texts.map(text =>
            text.id === textId ? { ...text, [field]: value } : text
          )
        };
      }
      return layout;
    }));
  };

  const addImage = (layoutId: string) => {
    setLayoutStructures(layoutStructures.map(layout => {
      if (layout.id === layoutId) {
        const newImage: LayoutStructureImageModel = {
          id: `image-${Date.now()}`,
          type: "picture",
          x: 0,
          y: 0,
          width: 200,
          height: 200,
          rotation: 0
        };
        return { ...layout, images: [...layout.images, newImage] };
      }
      return layout;
    }));
  };

  return (
    <Modal show={isShow} size="xl" onHide={() => onClickClose()} fullscreen>
      <Modal.Header closeButton>레이아웃 추가</Modal.Header>
      <Modal.Body>
        <Stack gap={2}>
          <FormGroup as={Row}>
            <Form.Label column sm={2}>블럭 타입</Form.Label>
            <Col>
              <Form.Select
                disabled={!!selectedLayout}
                value={blockType}
                onChange={(e) => {
                  setBlockType(e.target.value);
                }}
                >
                {getBlockTypeConfigs()?.map((val) => {
                  return (
                    <option value={val.blockType} key={val.blockType}>{val.blockType}</option>
                  );
                })}
              </Form.Select>
            </Col>
          </FormGroup>
          <FormGroup as={Row}>
            <Form.Label column sm={2}>레이아웃 타입</Form.Label>
            <Col>
              <Form.Control
                disabled={!!selectedLayout}
                size="sm"
                type="text"
                value={layoutType}
                placeholder="영어와 숫자로 이루어진 중복되지 않은 값으로 입력해주세요"
                onChange={(e) => setLayoutType(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
              />
            </Col>
          </FormGroup>
          <Row>
            <Col>
              {/* 탭 영역 */}
              <Stack direction="horizontal" gap={1}>
                {layoutStructures.map(layout => (
                  <ToggleButton
                    key={layout.id}
                    onClick={() => setActiveLayoutId(layout.id)}
                    type="checkbox"
                    variant="outline-primary"
                    id={layout.id}
                    value={layout.id}
                    checked={activeLayoutId === layout.id}
                  >
                    {layout.id}
                    <CloseButton onClick={() => closeLayout(layout.id)} />
                  </ToggleButton>
                ))}
                <Button variant="secondary" onClick={addLayout}>+</Button>
              </Stack>
              {/* 선택된 레이아웃만 렌더링 */}
              {layoutStructures
                ?.filter(layout => layout.id === activeLayoutId)
                ?.map(layout => (
                  <LayoutRenderer
                    key={layout.id}
                    layout={layout} // 단일 레이아웃 전달
                    setLayout={(updated) => {
                      setLayoutStructures(prev =>
                        prev.map(l => (l.id === layout.id ? updated : l))
                      );
                    }}
                    backgroundImage=""
                  />
                ))}
            </Col>
            <Col>
              {layoutStructures.filter(layout => layout.id === activeLayoutId).map(layout => (
                <Stack key={layout.id} gap={1}>
                  <InputGroup size="sm">
                    <InputGroup.Text>ID</InputGroup.Text>
                    <Form.Control
                      type="text"
                      defaultValue={layout.id}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setLayoutStructures(layoutStructures.map(l =>
                            l.id === layout.id ? { ...layout, id: e.currentTarget.value } : l
                          ));
                          setActiveLayoutId(e.currentTarget.value);
                        }
                      }}
                      onBlur={(e) => {
                        setLayoutStructures(layoutStructures.map(l =>
                          l.id === layout.id ? { ...layout, id: e.target.value } : l
                        ));
                        setActiveLayoutId(e.currentTarget.value);
                      }}
                    />
                    <InputGroup.Text>Type</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={layout.type}
                      onChange={(e) => {
                        setLayoutStructures(layoutStructures.map(l => l.id === layout.id
                          ? { ...layout, type: e.target.value }
                          : l));
                      }} />
                  </InputGroup>
                  <InputGroup size="sm">
                    <InputGroup.Text>W</InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={layout.width}
                      onChange={(e) => {
                        setLayoutStructures(layoutStructures.map(l => l.id === layout.id
                          ? { ...layout, width: parseInt(e.target.value) }
                          : l));
                      }} />
                    <InputGroup.Text>H</InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={layout.height}
                      onChange={(e) => {
                        setLayoutStructures(layoutStructures.map(l => l.id === layout.id
                          ? { ...layout, height: parseInt(e.target.value) }
                          : l));
                      }} />
                  </InputGroup>
                  <Stack direction="horizontal" gap={1}>
                    <Button variant="outline-primary" onClick={() => addImage(layout.id)}>Add Image</Button>
                    <Button variant="outline-primary" onClick={() => addText(layout.id)}>Add Text</Button>
                  </Stack>
                  {/* Texts 편집 폼 */}
                  <Tabs defaultActiveKey={"images"}>
                    <Tab eventKey={"images"} title="Images" style={{height: "80vh", overflowY: "auto"}}>
                      {layout.images.map(image => (
                        <Stack key={image.id} gap={1} className="border rounded p-1">
                          <InputGroup size="sm">
                            <InputGroup.Text>ID</InputGroup.Text>
                            <Form.Control
                              type="text"
                              defaultValue={image.id}
                              onBlur={(e) => {
                                const newImage: LayoutStructureImageModel = { ...image, id: e.target.value };
                                  setLayoutStructures(layoutStructures.map(l =>
                                    l.id === layout.id
                                      ? { ...l, images: l.images.map(img => img.id === image.id ? newImage : img) }
                                      : l
                                  ));
                              }}
                            />
                          </InputGroup>
                          <Form.Text>Position</Form.Text>
                          <InputGroup size="sm">
                            <InputGroup.Text>X</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={image.x}
                              onChange={(e) => {
                                const newImage: LayoutStructureImageModel = { ...image, x: parseFloat(e.target.value) };
                                setLayoutStructures(layoutStructures.map(l =>
                                  l.id === layout.id
                                    ? { ...l, images: l.images.map(img => img.id === image.id ? newImage : img) }
                                    : l
                                ));
                              }}
                            />
                            <InputGroup.Text>Y</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={image.y}
                              onChange={(e) => {
                                const newImage: LayoutStructureImageModel = { ...image, y: parseFloat(e.target.value) };
                                setLayoutStructures(layoutStructures.map(l =>
                                  l.id === layout.id
                                    ? { ...l, images: l.images.map(img => img.id === image.id ? newImage : img) }
                                    : l
                                ));
                              }}
                            />
                            <InputGroup.Text>W</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={image.width}
                              onChange={(e) => {
                                const newImage: LayoutStructureImageModel = { ...image, width: parseFloat(e.target.value) };
                                setLayoutStructures(layoutStructures.map(l =>
                                  l.id === layout.id
                                    ? { ...l, images: l.images.map(img => img.id === image.id ? newImage : img) }
                                    : l
                                ));
                              }}
                            />
                            <InputGroup.Text>H</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={image.height}
                              onChange={(e) => {
                                const newImage: LayoutStructureImageModel = { ...image, height: parseFloat(e.target.value) };
                                setLayoutStructures(layoutStructures.map(l =>
                                  l.id === layout.id
                                    ? { ...l, images: l.images.map(img => img.id === image.id ? newImage : img) }
                                    : l
                                ));
                              }}
                            />
                            <InputGroup.Text>R</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={image.rotation}
                              onChange={(e) => {
                                const newImage: LayoutStructureImageModel = { ...image, rotation: parseFloat(e.target.value) };
                                setLayoutStructures(layoutStructures.map(l =>
                                  l.id === layout.id
                                    ? { ...l, images: l.images.map(img => img.id === image.id ? newImage : img) }
                                    : l
                                ));
                              }}
                            />
                          </InputGroup>
                        </Stack>
                      ))}
                    </Tab>
                    <Tab eventKey="texts" title="Texts"  style={{height: "80vh", overflowY: "auto"}}>
                      {layout.texts.map(text => (
                        <Stack key={text.id} gap={1} className="border rounded p-1">
                          <InputGroup size="sm">
                            <InputGroup.Text>ID</InputGroup.Text>
                            <Form.Control
                              type="text"
                              defaultValue={text.id}
                              onBlur={(e) => updateText(layout.id, text.id, "id", e.target.value)}
                            />
                          </InputGroup>
                          <InputGroup size="sm">
                            <InputGroup.Text>Text</InputGroup.Text>
                            <Form.Control
                              type="text"
                              value={text.text}
                              onChange={(e) => updateText(layout.id, text.id, "text", e.target.value)}
                            />
                          </InputGroup>

                          <Form.Text>Position</Form.Text>
                          <InputGroup size="sm">
                            <InputGroup.Text>X</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.x}
                              onChange={(e) => updateText(layout.id, text.id, "x", parseFloat(e.target.value))}
                            />
                            <InputGroup.Text>Y</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.y}
                              onChange={(e) => updateText(layout.id, text.id, "y", parseFloat(e.target.value))}
                            />
                            <InputGroup.Text>W</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.width}
                              onChange={(e) => updateText(layout.id, text.id, "width", parseFloat(e.target.value))}
                            />
                            <InputGroup.Text>H</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.height}
                              onChange={(e) => updateText(layout.id, text.id, "height", parseFloat(e.target.value))}
                            />
                            <InputGroup.Text>R</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.rotation}
                              onChange={(e) => updateText(layout.id, text.id, "rotation", parseFloat(e.target.value))}
                            />
                          </InputGroup>

                          {/* Font */}
                          <Form.Text>Font</Form.Text>
                          <InputGroup size="sm">
                            <InputGroup.Text>Family</InputGroup.Text>
                            <Form.Control
                              type="text"
                              value={text.font.family}
                              onChange={(e) => {
                                const newFont: FontStyle = { ...text.font, family: e.target.value };
                                updateText(layout.id, text.id, "font", newFont);
                              }}
                            />
                            <InputGroup.Text>Size</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.font.size}
                              onChange={(e) => {
                                const newFont: FontStyle = { ...text.font, size: parseFloat(e.target.value) };
                                updateText(layout.id, text.id, "font", newFont);
                              }}
                            />
                            <InputGroup.Text>Color</InputGroup.Text>
                            <Form.Control
                              type="color"
                              value={text.font.color}
                              onChange={(e) => {
                                const newFont: FontStyle = { ...text.font, color: e.target.value };
                                updateText(layout.id, text.id, "font", newFont);
                              }}
                            />
                          </InputGroup>

                          {/* Shadow */}
                          <Form.Text>Shadow</Form.Text>
                          <InputGroup size="sm">
                            <InputGroup.Text>Color</InputGroup.Text>
                            <Form.Control
                              type="color"
                              value={text.shadow.color}
                              onChange={(e) => {
                                const newShadow: ShadowStyle = { ...text.shadow, color: e.target.value };
                                updateText(layout.id, text.id, "shadow", newShadow);
                              }}
                            />
                            <InputGroup.Text>OffsetX</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.shadow.offsetX}
                              onChange={(e) => {
                                const newShadow: ShadowStyle = { ...text.shadow, offsetX: parseFloat(e.target.value) };
                                updateText(layout.id, text.id, "shadow", newShadow);
                              }}
                            />
                            <InputGroup.Text>OffsetY</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.shadow.offsetY}
                              onChange={(e) => {
                                const newShadow: ShadowStyle = { ...text.shadow, offsetY: parseFloat(e.target.value) };
                                updateText(layout.id, text.id, "shadow", newShadow);
                              }}
                            />
                            <InputGroup.Text>Blur</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.shadow.blur}
                              onChange={(e) => {
                                const newShadow: ShadowStyle = { ...text.shadow, blur: parseFloat(e.target.value) };
                                updateText(layout.id, text.id, "shadow", newShadow);
                              }}
                            />
                          </InputGroup>

                          {/* Constraint */}
                          <Form.Text>Constraint</Form.Text>
                          <InputGroup size="sm">
                            <InputGroup.Text>Limit Length</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={text.constraint.limitLength}
                              onChange={(e) => {
                                const newConstraint: TextConstraint = { ...text.constraint, limitLength: parseInt(e.target.value) };
                                updateText(layout.id, text.id, "constraint", newConstraint);
                              }}
                            />
                            <InputGroup.Text>IsDateFormat</InputGroup.Text>
                            <InputGroup.Checkbox
                              type="checkbox"
                              checked={text.constraint.isDateFormat}
                              onChange={(e) => {
                                const newConstraint: TextConstraint = { ...text.constraint, isDateFormat: e.target.checked };
                                updateText(layout.id, text.id, "constraint", newConstraint);
                              }}
                            />
                            <InputGroup.Text>Date Format</InputGroup.Text>
                            <Form.Control
                              type="text"
                              value={text.constraint.dateFormat ?? ""}
                              onChange={(e) => {
                                const newConstraint: TextConstraint = { ...text.constraint, dateFormat: e.target.value };
                                updateText(layout.id, text.id, "constraint", newConstraint);
                              }}
                            />
                          </InputGroup>
                        </Stack>
                      ))}
                    </Tab>
                  </Tabs>
                </Stack>
              ))}
            </Col>
          </Row>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={!selectedLayout && !layoutType}
          variant="primary"
          onClick={() => {
            if (!selectedLayout) {
              onClickCreate({
                layoutId: "",
                layouts: layoutStructures,
                blockType: blockType,
                layoutType: layoutType,
                name: ""
              });
            } else {
              onClickSave({
                ...selectedLayout,
                layouts: layoutStructures
              });
            }
          }}>{t("button-save")}</Button>
        <Button variant="secondary" onClick={(() => onClickClose())}>{t("button-close")}</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default LayoutEditModal;